import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { LogOut, User, Menu, X, ListChecks } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import NotificationsBell from "./NotificationsBell";
import ThemeToggle from "./ThemeToggle";

const Header = () => {
  const location = useLocation();
  const { user, isAdmin, signOut } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const links = [
    { to: "/", label: "Home" },
    { to: "/rotations", label: "Rotations" },
    { to: "/residencies", label: "Residencies" },
    { to: "/fellowships", label: "Fellowships" },
    { to: "/internships", label: "Internships" },
    { to: "/leaderboard", label: "Leaderboard" },
  ];


  const closeMobile = () => setMobileOpen(false);

  return (
    <header className="sticky top-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-md">
      <div className="container flex h-16 items-center justify-between gap-2">
        <Link to="/" onClick={closeMobile} className="flex items-center gap-2 text-foreground min-w-0">
          <img src="/logo.png" alt="Vecurate" className="w-12 h-12 sm:w-16 sm:h-16 object-contain shrink-0" />
          <span className="font-serif text-lg sm:text-xl truncate">Vecurate</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1">
          {links.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={cn(
                "px-3 py-2 rounded-md text-sm font-medium transition-colors",
                location.pathname === link.to
                  ? "bg-secondary text-secondary-foreground"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              )}
            >
              {link.label}
            </Link>
          ))}
          <ThemeToggle />
          {user ? (
            <div className="flex items-center gap-1 ml-2">
              <NotificationsBell userId={user.id} />
              <Link to="/activity">
                <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground">
                  <ListChecks className="w-4 h-4" />
                  <span className="hidden lg:inline">Activity</span>
                </Button>
              </Link>
              <Link to="/profile">
                <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground">
                  <User className="w-4 h-4" />
                  {isAdmin && <span className="text-xs text-primary font-medium">Admin</span>}
                </Button>
              </Link>
              <Button variant="ghost" size="sm" onClick={signOut} className="text-muted-foreground">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 ml-3">
              <Link to="/signup">
                <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-5">
                  Sign Up
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" size="sm" className="rounded-full px-5 border-border">
                  Log In
                </Button>
              </Link>
            </div>
          )}
        </nav>

        {/* Mobile right side */}
        <div className="md:hidden flex items-center gap-1">
          <ThemeToggle />
          {user && <NotificationsBell userId={user.id} />}
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="p-2 rounded-md text-foreground hover:bg-muted"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <nav className="md:hidden border-t border-border/50 bg-background">
          <div className="container py-3 flex flex-col gap-1">
            {links.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={closeMobile}
                className={cn(
                  "px-3 py-2 rounded-md text-sm font-medium transition-colors",
                  location.pathname === link.to
                    ? "bg-secondary text-secondary-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                )}
              >
                {link.label}
              </Link>
            ))}
            <div className="h-px bg-border/50 my-2" />
            {user ? (
              <div className="flex flex-col gap-1">
                <Link to="/activity" onClick={closeMobile}>
                  <Button variant="ghost" size="sm" className="w-full justify-start gap-2 text-muted-foreground">
                    <ListChecks className="w-4 h-4" />
                    My Activity
                  </Button>
                </Link>
                <Link to="/profile" onClick={closeMobile}>
                  <Button variant="ghost" size="sm" className="w-full justify-start gap-2 text-muted-foreground">
                    <User className="w-4 h-4" />
                    Profile
                    {isAdmin && <span className="text-xs text-primary font-medium ml-1">(Admin)</span>}
                  </Button>
                </Link>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { closeMobile(); signOut(); }}
                  className="w-full justify-start gap-2 text-muted-foreground"
                >
                  <LogOut className="w-4 h-4" />
                  Sign out
                </Button>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <Link to="/signup" onClick={closeMobile}>
                  <Button size="sm" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full">
                    Sign Up
                  </Button>
                </Link>
                <Link to="/login" onClick={closeMobile}>
                  <Button variant="outline" size="sm" className="w-full rounded-full border-border">
                    Log In
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </nav>
      )}
    </header>
  );
};

export default Header;
